/**
 * StreamZoneBlock — Full stream zone: 16:9 player, OBS overlay, earn bar, ticker
 *
 * Renders the left-column stream experience from the CouRageJD/7-Eleven wireframes.
 * Works in both live mode (channelUrl) and demo mode (placeholderImageUrl).
 *
 * Layout:
 *   ┌─────────────────────────────────┐
 *   │  16:9 Player (embed or image)   │
 *   │  [OBS overlays: 4 corners]      │
 *   ├─────────────────────────────────┤
 *   │  Stream Bar (title + live + chat)│
 *   ├─────────────────────────────────┤
 *   │  Earn Bar (XP pills, scrollable)│
 *   ├─────────────────────────────────┤
 *   │  Live Feed Ticker (infinite)    │
 *   └─────────────────────────────────┘
 */

import { isHttpUrl, parseStreamUrl } from "@/widgets/shared";
import "./StreamZoneBlock.scss";

/**
 * Must stay in sync with StreamZoneConfig in admin/src/pages/hub-builder/fields/StreamZoneConfigField.tsx.
 * Both interfaces are typed locally (web does not import from @devour/models) — update them together.
 */
interface SurfaceStreamZoneConfig {
    channelUrl?: string;
    placeholderImageUrl?: string;
    showChat?: boolean;
    hubBadgeEnabled?: boolean;
    hubBadgeMemberCount?: string;
    activityNotificationEnabled?: boolean;
    activityNotificationText?: string;
    challengeProgressEnabled?: boolean;
    challengeProgressLabel?: string;
    challengeProgressName?: string;
    challengeProgressValue?: number;
    challengeProgressStat?: string;
    sponsorPillsEnabled?: boolean;
    sponsorPillSponsors?: Array<{ iconUrl?: string; name: string }>;
    streamTitle?: string;
    chatButtonUrl?: string;
    earnItems?: Array<{ icon?: string; label: string; points: number; active?: boolean }>;
    tickerItems?: Array<{ text: string; color?: "orange" | "green" | "red" }>;
    tickerSpeed?: number;
}

interface StreamZoneBlockProps {
    config?: SurfaceStreamZoneConfig;
    sectionTitle?: string;
    /** Hub name shown in the badge overlay */
    hubName?: string;
    /** Hub logo URL shown in the badge overlay */
    hubLogoUrl?: string;
}

export function StreamZoneBlock({ config, sectionTitle, hubName, hubLogoUrl }: StreamZoneBlockProps) {
    /*
     * `channelUrl` is admin-authored and unvalidated on write, so it goes
     * through the shared parser: a non-http(s) scheme parses to null and an
     * unrecognized host to "unknown", and neither is embedded. Refusing
     * "unknown" is what keeps this from iframing an arbitrary host.
     */
    const parsedStream = config?.channelUrl ? parseStreamUrl(config.channelUrl) : null;
    const embedUrl = parsedStream && parsedStream.platform !== "unknown" ? parsedStream.embedUrl : "";
    const isLive = !!embedUrl;
    const tickerItems = config?.tickerItems || [];
    const earnItems = config?.earnItems || [];
    const tickerSpeed = config?.tickerSpeed || 25;

    return (
        <div className="stream-zone">
            {sectionTitle && <h2 className="section-title">{sectionTitle}</h2>}

            {/* Player + OBS Overlay */}
            <div className="stream-zone__player-wrap">
                {isLive
                    ? <iframe
                        className="stream-zone__player"
                        src={embedUrl}
                        allowFullScreen
                        allow="autoplay; encrypted-media"
                        title="Live Stream"
                    />
                    : config?.placeholderImageUrl
                        ? <img
                            className="stream-zone__player stream-zone__player--placeholder"
                            src={config.placeholderImageUrl}
                            alt="Stream preview"
                        />
                        : <div className="stream-zone__player stream-zone__player--empty">
                            <span className="stream-zone__offline-label">OFFLINE</span>
                        </div>
                }

                {/* OBS Overlay */}
                <div className="stream-zone__overlay">
                    {/* Top-left: Hub badge */}
                    {config?.hubBadgeEnabled !== false &&
                        <div className="stream-zone__overlay-badge stream-zone__overlay-badge--tl">
                            {hubLogoUrl && <img src={hubLogoUrl} alt={hubName || "Hub"} className="stream-zone__badge-logo" />}
                            <div className="stream-zone__badge-text">
                                {hubName && <span className="stream-zone__badge-name">{hubName}</span>}
                                {config?.hubBadgeMemberCount &&
                                    <span className="stream-zone__badge-members">{config.hubBadgeMemberCount} members</span>
                                }
                            </div>
                        </div>
                    }

                    {/* Top-right: Activity notification */}
                    {config?.activityNotificationEnabled !== false && config?.activityNotificationText &&
                        <div className="stream-zone__overlay-notification stream-zone__overlay-notification--tr">
                            <span className="stream-zone__notification-dot" />
                            <span>{config.activityNotificationText}</span>
                        </div>
                    }

                    {/* Bottom-left: Challenge progress */}
                    {config?.challengeProgressEnabled !== false && config?.challengeProgressName &&
                        <div className="stream-zone__overlay-progress stream-zone__overlay-progress--bl">
                            {config.challengeProgressLabel &&
                                <span className="stream-zone__progress-label">{config.challengeProgressLabel}</span>
                            }
                            <span className="stream-zone__progress-name">{config.challengeProgressName}</span>
                            {config.challengeProgressValue !== undefined &&
                                <div className="stream-zone__progress-bar">
                                    <div
                                        className="stream-zone__progress-fill"
                                        style={{ width: `${Math.min(100, Math.max(0, config.challengeProgressValue))}%` }}
                                    />
                                </div>
                            }
                            {config.challengeProgressStat &&
                                <span className="stream-zone__progress-stat">{config.challengeProgressStat}</span>
                            }
                        </div>
                    }

                    {/* Bottom-right: Sponsor pills */}
                    {config?.sponsorPillsEnabled !== false && config?.sponsorPillSponsors && config.sponsorPillSponsors.length > 0 &&
                        <div className="stream-zone__overlay-sponsors stream-zone__overlay-sponsors--br">
                            {config.sponsorPillSponsors.slice(0, 3).map((s, i) =>
                                <div key={i} className="stream-zone__sponsor-pill">
                                    {s.iconUrl && <img src={s.iconUrl} alt={s.name} className="stream-zone__sponsor-icon" />}
                                    <span>{s.name}</span>
                                </div>)}
                        </div>
                    }
                </div>
            </div>

            {/* Stream Bar */}
            <div className="stream-zone__stream-bar">
                <div className="stream-zone__stream-bar-left">
                    {isLive &&
                        <span className="stream-zone__live-badge">LIVE</span>
                    }
                    {config?.streamTitle &&
                        <span className="stream-zone__stream-title">{config.streamTitle}</span>
                    }
                </div>
                {/*
                  * `isHttpUrl` gate: `chatButtonUrl` is admin-authored and
                  * unvalidated on write, exactly like `channelUrl` above, so a
                  * `javascript:` URL would run on click. Guarding one field in
                  * this file and not the other was the whole gap.
                  */}
                {isHttpUrl(config?.chatButtonUrl) &&
                    <a
                        href={config!.chatButtonUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="stream-zone__chat-btn"
                    >
                        CHAT
                    </a>
                }
            </div>

            {/* Earn Bar */}
            {earnItems.length > 0 &&
                <div className="stream-zone__earn-bar">
                    {earnItems.map((item, i) =>
                        <div
                            key={i}
                            className={`stream-zone__earn-pill${item.active === false ? " stream-zone__earn-pill--inactive" : ""}`}
                        >
                            {item.icon && <i className={`stream-zone__earn-icon fas fa-${item.icon}`} />}
                            <span className="stream-zone__earn-label">{item.label}</span>
                            <span className="stream-zone__earn-points">+{item.points} XP</span>
                        </div>)}
                </div>
            }

            {/* Live Feed Ticker */}
            {tickerItems.length > 0 &&
                <div className="stream-zone__ticker">
                    <div
                        className="stream-zone__ticker-track"
                        style={{ animationDuration: `${tickerSpeed}s` }}
                    >
                        {/* Items are doubled so the CSS marquee animation (translateX -50%)
                            can loop seamlessly: the second copy fills the gap as the first scrolls out */}
                        {[...tickerItems, ...tickerItems].map((item, i) =>
                            <span
                                key={i}
                                className={`stream-zone__ticker-item stream-zone__ticker-item--${item.color || "orange"}`}
                            >
                                {item.text}
                                <span className="stream-zone__ticker-sep">•</span>
                            </span>)}
                    </div>
                </div>
            }
        </div>
    );
}
